import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material'

import { PlainTextParagraphs } from '../../../../components/PlainTextParagraphs'
import { sanitizeHtml } from '../../../../helpers/dom'
import { sectionLabelSx } from '../helpers/typographyStyles'

// Which taxonomy level the copy describes. Decides the heading, and with it how
// the copy is rendered: blueprint descriptions are authored as HTML in the
// blueprint glossary, event type descriptions are plain text in the language map.
type GlossaryDescriptionKind = 'blueprint' | 'eventType'

const TITLE_KEY: Record<GlossaryDescriptionKind, string> = {
  blueprint: 'market-analytics:detail_panel_what_this_blueprint_is',
  eventType: 'market-analytics:detail_panel_what_this_event_type_is',
}

interface GlossaryDescriptionCardProps {
  kind: GlossaryDescriptionKind
  // Resolved (already language-picked) analyst description.
  description: string
  isLoading?: boolean
}

/**
 * "What this … is" card from the v14 mockup — the analyst-written glossary
 * description on its own card so it can sit alongside the example screenshots.
 *
 * Started out blueprint-only; the event type page needed the same treatment for
 * its own copy (analyst feedback on the v14 staging build), which is what `kind`
 * is for.
 */
export const GlossaryDescriptionCard: React.FC<GlossaryDescriptionCardProps> = ({ kind, description, isLoading }) => {
  const { t } = useTranslation()

  // sanitizeHtml (DOMPurify) is expensive; memoize on the raw description.
  const sanitizedDescription = useMemo(() => (description && kind === 'blueprint' ? sanitizeHtml(description) : ''), [description, kind])

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography sx={{ ...sectionLabelSx, mb: 1 }}>{t(TITLE_KEY[kind])}</Typography>
        {isLoading ? (
          <Stack spacing={0.75}>
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="94%" />
            <Skeleton variant="text" width="88%" />
            <Skeleton variant="text" width="62%" />
          </Stack>
        ) : !description ? (
          <Typography variant="body2" color="text.secondary">
            {t('market-analytics:detail_panel_no_description')}
          </Typography>
        ) : kind === 'blueprint' ? (
          <Box
            sx={{
              color: 'text.primary',
              typography: 'body2',
              '& p': { m: 0 },
              '& p + p': { mt: 1 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        ) : (
          <PlainTextParagraphs text={description} />
        )}
      </CardContent>
    </Card>
  )
}
